import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, fieldErrors } from "../../../api/client";
import { venueApi } from "../../../api/resources";
import { clockApi } from "../../../api/clock";
import type { GeocodeCandidate, VenueAdmin, VenueInput } from "../../../api/types";
import { PageHeader } from "../../../components/ComingSoon";
import { Button, DemoBadge, ErrorText, Field, FullScreenLoader, TextInput } from "../../../components/ui";
import { AddressLookup } from "./AddressLookup";
import { TimezoneField } from "./TimezoneField";
import { FEET_TO_METERS, VenueMap } from "./VenueMap";

type Form = Record<"name" | "street_address" | "city" | "state" | "postal_code" | "country" | "lat" | "lng" | "radius" | "timezone" | "accuracy", string>;

const EMPTY: Form = { name: "", street_address: "", city: "", state: "", postal_code: "", country: "US", lat: "", lng: "", radius: "", timezone: "", accuracy: "" };
const ADDRESS_FIELDS: [keyof Form, string][] = [["street_address", "Street address"], ["city", "City"], ["state", "State"], ["postal_code", "Postal code"], ["country", "Country"]];

function fromVenue(v: VenueAdmin): Form {
  return { name: v.name, street_address: v.street_address ?? "", city: v.city ?? "", state: v.state ?? "", postal_code: v.postal_code ?? "",
    country: v.country ?? "US", lat: String(v.latitude), lng: String(v.longitude), radius: String(v.geofence_radius_feet), timezone: v.timezone,
    accuracy: v.gps_min_accuracy_m_override == null ? "" : String(v.gps_min_accuracy_m_override) };
}

function validate(f: Form, zones: string[]): Record<string, string> {
  const e: Record<string, string> = {};
  const lat = Number(f.lat), lng = Number(f.lng), r = Number(f.radius);
  if (!f.name.trim()) e.name = "Name is required";
  if (f.name.length > 120) e.name = "Name must be 120 characters or fewer";
  if (f.lat === "" || isNaN(lat) || lat < -90 || lat > 90) e.latitude = "Latitude must be between -90 and 90";
  if (f.lng === "" || isNaN(lng) || lng < -180 || lng > 180) e.longitude = "Longitude must be between -180 and 180";
  if (f.radius === "") e.geofence_radius_feet = "Geofence radius is required";
  else if (!Number.isInteger(r) || r < 50 || r > 5280) e.geofence_radius_feet = "Enter a whole number from 50 to 5,280 feet";
  const acc = Number(f.accuracy);
  if (f.accuracy !== "" && (!Number.isInteger(acc) || acc < 5 || acc > 1000)) e.gps_min_accuracy_m_override = "Enter a whole number from 5 to 1,000 m, or leave blank";
  if (!f.timezone || !zones.includes(f.timezone)) e.timezone = "Confirm a valid IANA timezone";
  return e;
}

export default function VenueFormPage() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form>(EMPTY);
  const [venue, setVenue] = useState<VenueAdmin | null>(null);
  const [zones, setZones] = useState<string[]>([]);
  const [globalAcc, setGlobalAcc] = useState<number | null>(null);
  useEffect(() => {
    clockApi.settings().then((c) => setGlobalAcc(Number(c.values.gps_min_accuracy_m) || null)).catch(() => setGlobalAcc(null));
  }, []);
  const [loading, setLoading] = useState(!!venueId);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    venueApi.timezones().then(setZones).catch(() => setZones([]));
    if (venueId) venueApi.get(venueId).then((v) => { setVenue(v); setForm(fromVenue(v)); }).catch((e) => setError(errorMessage(e))).finally(() => setLoading(false));
  }, [venueId]);

  const ERR_KEY: Partial<Record<keyof Form, string>> = { lat: "latitude", lng: "longitude", radius: "geofence_radius_feet", accuracy: "gps_min_accuracy_m_override" };
  const set = (k: keyof Form, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => {
      const key = ERR_KEY[k] ?? k;
      if (!(key in e)) return e;
      const rest = { ...e };
      delete rest[key];
      return rest;
    });
  };
  const setPin = useCallback((lat: number, lng: number) => setForm((f) => ({ ...f, lat: lat.toFixed(6), lng: lng.toFixed(6) })), []);
  const pick = (c: GeocodeCandidate) => setForm((f) => ({
    ...f, street_address: c.street_address ?? f.street_address, city: c.city ?? f.city, state: c.state ?? f.state,
    postal_code: c.postal_code ?? f.postal_code, country: c.country ?? f.country, lat: c.latitude.toFixed(6), lng: c.longitude.toFixed(6),
  }));

  const lat = form.lat !== "" && !isNaN(Number(form.lat)) ? Number(form.lat) : null;
  const lng = form.lng !== "" && !isNaN(Number(form.lng)) ? Number(form.lng) : null;
  const radius = form.radius !== "" && !isNaN(Number(form.radius)) ? Number(form.radius) : null;
  const address = [form.street_address, form.city, form.state, form.postal_code, form.country].filter(Boolean);
  const query = (form.street_address || form.city ? address : [form.name, ...address]).join(", ");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const local = validate(form, zones);
    setErrors(local);
    setError(null);
    if (Object.keys(local).length) return;
    const body: VenueInput = {
      name: form.name, street_address: form.street_address || null, city: form.city || null, state: form.state || null,
      postal_code: form.postal_code || null, country: form.country || "US", formatted_address: null,
      latitude: Number(form.lat), longitude: Number(form.lng), timezone: form.timezone, geofence_radius_feet: Number(form.radius),
      gps_min_accuracy_m_override: form.accuracy === "" ? null : Number(form.accuracy),
    };
    setBusy(true);
    try {
      const saved = venueId ? await venueApi.update(venueId, body) : await venueApi.create(body);
      toast.success(venueId ? "Venue updated" : `Venue "${saved.name}" created`);
      navigate("/admin/venues");
    } catch (err) {
      const f = fieldErrors(err);
      if (Object.keys(f).length) setErrors(f);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <FullScreenLoader />;
  const err = (k: string) => errors[k] && <p data-testid={`venue-${k.replace(/_/g, "-")}-error`} className="text-xs text-red-300">{errors[k]}</p>;

  return (
    <section data-testid="venue-form-page" className="animate-rise">
      <Link to="/admin/venues" data-testid="venue-back-link" className="mb-6 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> All venues</Link>
      <PageHeader title={venue ? venue.name : "Create venue"}>
        {venue && <DemoBadge show={venue.is_demo} testId="venue-form-demo-badge" />}
      </PageHeader>
      <form onSubmit={submit} noValidate className="grid gap-10 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="space-y-6">
          <Field label="Venue name" htmlFor="venue-name">
            <TextInput id="venue-name" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} data-testid="venue-name-input" />
            {err("name")}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            {ADDRESS_FIELDS.map(([k, label]) => (
              <Field key={k} label={label} htmlFor={`venue-${k}`}>
                <TextInput id={`venue-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`venue-${k.replace("_", "-")}-input`} />
              </Field>
            ))}
          </div>
          <AddressLookup query={query} onPick={pick} />
          <div>
            <p className="label-caps mb-3 text-zinc-400">Timezone</p>
            <TimezoneField value={form.timezone} onChange={(tz) => set("timezone", tz)} lat={lat} lng={lng} zones={zones} />
            {err("timezone")}
          </div>
          <Field label="Geofence radius (feet) — required" htmlFor="venue-radius">
            <TextInput id="venue-radius" type="number" inputMode="numeric" min={50} max={5280} step={1} placeholder="e.g. 1000"
              value={form.radius} onChange={(e) => set("radius", e.target.value)} data-testid="venue-radius-input" />
            <p data-testid="venue-radius-meters" className="text-xs text-zinc-500">
              Whole number, 50–5,280 ft.{radius ? ` ≈ ${Math.round(radius * FEET_TO_METERS).toLocaleString()} m` : ""}
            </p>
            {err("geofence_radius_feet")}
          </Field>
          <Field label="GPS accuracy override (meters) — optional" htmlFor="venue-accuracy">
            <TextInput id="venue-accuracy" type="number" inputMode="numeric" min={5} max={1000} step={1} placeholder={globalAcc ? `Blank = global ${globalAcc} m` : "Blank = global"}
              value={form.accuracy} onChange={(e) => set("accuracy", e.target.value)} data-testid="venue-accuracy-override-input" />
            <p data-testid="venue-accuracy-effective" className="text-xs text-zinc-500">
              Effective: {form.accuracy !== "" ? `${form.accuracy} m (venue override)` : globalAcc ? `${globalAcc} m (global setting)` : "global setting"}. Larger allows less precise fixes (e.g. indoor stadiums).
            </p>
            {err("gps_min_accuracy_m_override")}
          </Field>
        </div>
        <div className="space-y-4">
          <VenueMap lat={lat} lng={lng} radiusFeet={radius} onChange={setPin} />
          <p className="text-xs text-zinc-500">Click the map to place the pin, or drag it. The red circle shows the geofence.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Latitude" htmlFor="venue-lat">
              <TextInput id="venue-lat" inputMode="decimal" value={form.lat} onChange={(e) => set("lat", e.target.value)} data-testid="venue-latitude-input" />
              {err("latitude")}
            </Field>
            <Field label="Longitude" htmlFor="venue-lng">
              <TextInput id="venue-lng" inputMode="decimal" value={form.lng} onChange={(e) => set("lng", e.target.value)} data-testid="venue-longitude-input" />
              {err("longitude")}
            </Field>
          </div>
          <ErrorText message={error} testId="venue-form-error" />
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => navigate("/admin/venues")} data-testid="venue-cancel-button">Cancel</Button>
            <Button type="submit" loading={busy} data-testid="venue-save-button">{venueId ? "Save changes" : "Create venue"}</Button>
          </div>
        </div>
      </form>
    </section>
  );
}
